import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, StatusBadge } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { canCancel, canDecide, safeHttpsUrl, slotLabel, statusLook, visitorTimeLabel } from "./bookings-model";
import type { Booking } from "./lead-types";
import type { Decision } from "./use-bookings";

interface BookingRowProps {
  booking: Booking;
  canEdit: boolean;
  /** Link to the lead sheet, or `null` when the booking has no lead. */
  leadHref: string | null;
  onDecide: (booking: Booking, decision: Decision, when: string) => void;
}

function ExternalLink({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-ink hover:underline">
      <Icon name="external-link" className="size-3" />
      {label}
    </a>
  );
}

function VisitorCell({ booking }: { booking: Booking }) {
  const { t } = useTranslation();
  const details = [
    booking.phone_last4 ? t("engage.bookings.phoneEnding", { digits: booking.phone_last4 }) : null,
    booking.party_size ? t("engage.bookings.party", { count: booking.party_size }) : null,
  ].filter(Boolean);
  return (
    <td>
      <div className="font-semibold">{booking.name}</div>
      <div className="text-xs text-fg-subtle">{booking.email}</div>
      {details.length > 0 ? <div className="text-xs text-fg-subtle">{details.join(" · ")}</div> : null}
      {booking.answers.length > 0 ? (
        <dl className="mt-1 max-w-56 text-xs text-fg-muted" data-testid="booking-answers">
          {booking.answers.map((answer) => (
            <div key={answer.key}>
              <dt className="inline font-semibold">{answer.label}: </dt>
              <dd className="inline">{answer.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {booking.notes ? <div className="mt-1 max-w-56 text-xs text-fg-muted">{t("engage.bookings.note", { note: booking.notes })}</div> : null}
    </td>
  );
}

/** One booking: when (workspace + visitor time zone), service, visitor, owner, status, Confirm / Decline (a request) or Cancel (a confirmed booking). */
export function BookingRow({ booking, canEdit, leadHref, onDecide }: BookingRowProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const slot = slotLabel(booking.slot_start, booking.slot_end, format.timeZone, format.locale);
  const visitor = visitorTimeLabel(booking, format.timeZone, format.locale);
  const look = statusLook(booking.status);
  const meetUrl = safeHttpsUrl(booking.meet_url);
  const calendarUrl = safeHttpsUrl(booking.calendar_url);
  const when = `${slot.date} · ${slot.time}`;
  const open = canDecide(booking, canEdit);
  const cancellable = canCancel(booking, canEdit);
  return (
    <tr data-testid="booking-row" data-status={booking.status}>
      <td className="whitespace-nowrap">
        <div className="font-semibold">{slot.date}</div>
        <div className="text-xs text-fg-muted">{slot.time}</div>
        {visitor ? <div className="mt-1 max-w-48 whitespace-normal text-xs text-fg-subtle">{t("engage.bookings.visitorTime", visitor)}</div> : null}
      </td>
      <td>
        <div className="font-semibold">{booking.service}</div>
        {booking.location ? (
          <div className="flex items-center gap-1 text-xs text-fg-subtle">
            <Icon name="map-pin" className="size-3 shrink-0" />
            {booking.location}
          </div>
        ) : null}
        <div className="mt-1 flex flex-col items-start gap-0.5">
          {calendarUrl ? <ExternalLink href={calendarUrl} label={t("engage.bookings.openCalendar")} /> : null}
          {meetUrl ? <ExternalLink href={meetUrl} label={t("engage.bookings.joinMeet")} /> : null}
        </div>
      </td>
      <VisitorCell booking={booking} />
      <td className={booking.owner_name ? "whitespace-nowrap" : "whitespace-nowrap italic text-fg-subtle"}>{booking.owner_name ?? t("engage.leads.unassigned")}</td>
      <td>
        <StatusBadge status={look.kind} icon={look.icon} label={t(`engage.bookings.status.${booking.status}`, { defaultValue: booking.status })} />
        {booking.decision_note ? <div className="mt-1 max-w-48 text-xs text-fg-subtle">{t("engage.bookings.decisionNote", { note: booking.decision_note })}</div> : null}
      </td>
      <td>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {open ? (
            <>
              <Button size="sm" aria-label={t("engage.bookings.confirmLabel", { name: booking.name })} onClick={() => { onDecide(booking, "confirm", when); }}>
                <Icon name="check" />
                {t("engage.bookings.confirm")}
              </Button>
              <Button size="sm" variant="secondary" aria-label={t("engage.bookings.declineLabel", { name: booking.name })} onClick={() => { onDecide(booking, "decline", when); }}>
                <Icon name="x" />
                {t("engage.bookings.decline")}
              </Button>
            </>
          ) : null}
          {cancellable ? (
            <Button size="sm" variant="secondary" aria-label={t("engage.bookings.cancelLabel", { name: booking.name })} onClick={() => { onDecide(booking, "cancel", when); }}>
              <Icon name="x-circle" />
              {t("engage.bookings.cancel")}
            </Button>
          ) : null}
          {leadHref ? (
            <Button asChild size="sm" variant="ghost">
              <AppLink href={leadHref}>
                <Icon name="user" />
                {t("engage.bookings.openLead")}
              </AppLink>
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
